import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, RefreshCcw } from 'lucide-react';
import { getCalendarBookingsApi, getBrandsApi, getSlotsApi } from '../utils/api';

// Read-only booking calendar for admins.
// Reuses the exact backend endpoint (/api/requests/calendar-bookings) and the
// same brand color coding as the Brand Manager calendar. This is a view-only
// component — no click handlers, no mutations, no permission changes.
const BookingCalendar = ({ initialMonth }) => {
  const [currentMonth, setCurrentMonth] = useState(initialMonth || new Date());
  const [bookings, setBookings] = useState({});
  const [brands, setBrands] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const monthName = currentMonth.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  const monthRange = useMemo(() => {
    const first = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const last = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    // Use local YYYY-MM-DD to match the backend's storage convention
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: iso(first), end: iso(last) };
  }, [currentMonth]);

  const loadBookings = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await getCalendarBookingsApi(monthRange.start, monthRange.end);
      setBookings(res.data || {});
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to load booking calendar', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [monthRange]);

  // Initial brands + slots load (rarely change during a session)
  useEffect(() => {
    (async () => {
      try {
        const [b, s] = await Promise.all([getBrandsApi(), getSlotsApi()]);
        setBrands(b.data || []);
        setSlots(s.data || []);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load brands/slots', e);
      }
    })();
  }, []);

  // Reload bookings whenever the visible month changes, plus auto-refresh
  // every 60 s so the calendar stays fresh without manual reload.
  useEffect(() => {
    loadBookings();
    const t = setInterval(() => loadBookings(true), 60_000);
    return () => clearInterval(t);
  }, [loadBookings]);

  // Build a 6-row (max) calendar grid for the visible month
  const days = useMemo(() => {
    const first = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const last = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const grid = [];
    for (let i = 0; i < first.getDay(); i += 1) grid.push(null);
    for (let d = 1; d <= last.getDate(); d += 1) grid.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d));
    return grid;
  }, [currentMonth]);

  const totalBookings = useMemo(
    () => Object.values(bookings).reduce((sum, dayMap) => sum + Object.keys(dayMap || {}).length, 0),
    [bookings],
  );

  return (
    <div className="p-4 md:p-6" data-testid="admin-booking-calendar">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500 text-white flex items-center justify-center">
            <CalendarIcon size={18} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">Booking Calendar</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              All brand bookings for {monthName} · {totalBookings} slot{totalBookings === 1 ? '' : 's'} booked
            </p>
          </div>
        </div>
        <button
          onClick={() => loadBookings()}
          disabled={refreshing || loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-teal-400 text-xs font-medium text-slate-700 dark:text-slate-200 disabled:opacity-50"
          data-testid="booking-calendar-refresh"
        >
          <RefreshCcw size={12} className={refreshing ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="bg-slate-800 dark:bg-slate-800 light:bg-white rounded-2xl p-4 md:p-6 border border-slate-700 dark:border-slate-700 light:border-slate-200">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
            className="p-2 hover:bg-slate-700 dark:hover:bg-slate-700 light:hover:bg-slate-100 rounded transition-colors"
            data-testid="booking-calendar-prev"
          >
            <ChevronLeft className="text-slate-200 dark:text-white light:text-slate-700" size={20} />
          </button>
          <span className="text-white dark:text-white light:text-slate-900 font-semibold" data-testid="booking-calendar-month-label">
            {monthName}
          </span>
          <button
            type="button"
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
            className="p-2 hover:bg-slate-700 dark:hover:bg-slate-700 light:hover:bg-slate-100 rounded transition-colors"
            data-testid="booking-calendar-next"
          >
            <ChevronRight className="text-slate-200 dark:text-white light:text-slate-700" size={20} />
          </button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400 text-sm">Loading calendar…</div>
        ) : (
          <>
            {/* Grid */}
            <div className="grid grid-cols-7 gap-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d} className="text-center text-xs font-medium text-slate-400 dark:text-slate-400 light:text-slate-600 py-2">
                  {d}
                </div>
              ))}
              {days.map((date, idx) => {
                if (!date) return <div key={`e-${idx}`} />;
                const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const isPast = date < today;
                const isToday = date.getTime() === today.getTime();
                const dayBookings = bookings[dateStr] || {};
                const bookedCount = Object.keys(dayBookings).length;

                return (
                  <div
                    key={dateStr}
                    className={`rounded-xl border-2 p-2 ${
                      isToday
                        ? 'border-teal-500 bg-teal-500/10'
                        : isPast
                          ? 'border-slate-700 dark:border-slate-700 light:border-slate-200 opacity-60'
                          : 'border-slate-700 dark:border-slate-700 light:border-slate-200'
                    }`}
                    data-testid={`booking-calendar-cell-${dateStr}`}
                  >
                    {/* Date + booked count */}
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-semibold ${isPast ? 'text-slate-500' : 'text-white dark:text-white light:text-slate-900'}`}>
                        {date.getDate()}
                      </span>
                      {bookedCount > 0 && (
                        <span className="text-[9px] font-bold text-slate-400 dark:text-slate-400 light:text-slate-600">
                          {bookedCount}/{slots.length || 4}
                        </span>
                      )}
                    </div>

                    {/* Slot pills */}
                    <div className="grid grid-cols-2 gap-0.5">
                      {(slots.slice(0, 4).length ? slots.slice(0, 4) : Array.from({ length: 4 }, (_, i) => ({ id: `slot_${i}`, time: '' }))).map((slot, slotIdx) => {
                        const booking = dayBookings[slot.id];
                        const isBooked = !!booking;
                        const status = booking?.status;
                        return (
                          <div
                            key={slot.id}
                            className={`h-5 flex items-center justify-center text-[9px] font-bold rounded ${
                              isBooked
                                ? 'text-white'
                                : 'bg-slate-700 dark:bg-slate-700 light:bg-slate-100 text-slate-400 dark:text-slate-400 light:text-slate-500'
                            }`}
                            style={isBooked ? {
                              backgroundColor: booking.brand_color,
                              // Pending bookings get a dashed outline
                              outline: status === 'Pending' ? '1px dashed #fbbf24' : 'none',
                              outlineOffset: -1,
                            } : {}}
                            title={
                              isBooked
                                ? `${booking.brand_name} — ${booking.slot_name || `Slot ${slotIdx + 1}`}${slot.time ? ` (${slot.time})` : ''}${status ? ` · ${status}` : ''}`
                                : `Slot ${slotIdx + 1}${slot.time ? ` — ${slot.time}` : ''} — Available`
                            }
                            data-testid={`booking-cell-slot-${dateStr}-${slot.id}`}
                          >
                            {isBooked ? booking.brand_abbr : `S${slotIdx + 1}`}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-6 pt-4 border-t border-slate-700 dark:border-slate-700 light:border-slate-200">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">Brand key</p>
              <div className="flex flex-wrap gap-2.5">
                {brands.map((b) => (
                  <div key={b.id} className="inline-flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 rounded-md" style={{ backgroundColor: b.color }} />
                    <span className="text-xs text-slate-300 dark:text-slate-300 light:text-slate-700">
                      {b.name} <span className="text-slate-500">({b.abbr})</span>
                    </span>
                  </div>
                ))}
                <div className="inline-flex items-center gap-1.5 ml-auto">
                  <span className="w-3.5 h-3.5 rounded-md border-2 border-dashed border-amber-400" />
                  <span className="text-xs text-slate-400">Pending approval</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingCalendar;
